import { useState } from 'react';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      setFormSubmitted(true);
      return;
    }

    const formData = new FormData(form);
    if (formData.get('message') && (formData.get('message') as string).length > 500) {
      setFormError('留言内容不能超过500个字符');
      setFormStatus('error');
      return;
    }

    try {
      const submitAddr = 'https://readdy.ai/api/form/d9rdcqee3oq4jqumtvtg';
      const response = await fetch(submitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: any;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormSubmitted(true);
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        if (msg.toLowerCase().includes('spam') || msg.includes('form data is spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(msg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    }
  };

  const milestones = [
    { year: '2020', title: '平台创立', desc: '玖玖视频正式上线，以高清流畅的视频播放体验切入市场。' },
    { year: '2021', title: '内容扩展', desc: '视频资源突破5000部，新增纪录片、教育、科技等多个垂直频道。' },
    { year: '2022', title: '用户突破', desc: '注册用户突破100万，月活跃用户50万，成为视频领域的新锐平台。' },
    { year: '2023', title: '技术升级', desc: '全面升级视频编码技术，支持4K超高清画质，CDN节点覆盖全国。' },
    { year: '2024', title: '生态完善', desc: '上线社区互动功能，完善分类体系和个性化推荐算法。' },
    { year: '2025', title: '快速发展', desc: '视频总量突破10000部，月活跃用户突破500万，跻身行业前列。' },
    { year: '2026', title: '全新启航', desc: '推出全新品牌形象和产品体验，持续为亿万用户提供优质视频服务。' },
  ];

  const testimonials = [
    { name: '张先生', role: '纪录片爱好者', content: '玖玖视频的画质太棒了，纪录片的资源也很丰富。我每天都会花一两个小时在这里看片，已经成为生活的一部分。', rating: 5 },
    { name: '李女士', role: '学生', content: '作为学生党，免费又高清的玖玖视频简直是宝藏平台。学习累了就看看美食和旅游频道，特别解压。', rating: 5 },
    { name: '王先生', role: '程序员', content: '科技频道的AI和编程教程对我帮助很大。播放流畅不卡顿，倍速播放功能也很好用，强烈推荐。', rating: 5 },
    { name: '赵女士', role: '健身教练', content: '健身频道的教程非常专业，画面清晰动作看得一清二楚。在家跟着练效果很好，已经推荐给了很多学员。', rating: 4 },
    { name: '陈先生', role: '音乐教师', content: '音乐频道的古典音乐鉴赏系列做得很用心，解说也通俗易懂。希望以后能多更新一些音乐教育类的内容。', rating: 5 },
    { name: '刘女士', role: '旅行博主', content: '旅行频道的攻略视频帮我规划了好几次旅行，极光那期简直太震撼了！视频质量真的没话说。', rating: 5 },
  ];

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '玖玖视频',
    url: 'https://www.jiujiu-video.com',
    description: '玖玖视频 - 高清视频在线观看平台，海量视频资源免费观看',
    foundingDate: '2020',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@jiujiu-video.com',
      telephone: '+86-400-888-9999',
      availableLanguage: ['Chinese'],
    },
  };

  return (
    <Layout>
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>

      <section className="pt-20 bg-background-50">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

          <div className="py-10 md:py-16 text-center">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
              关于玖玖视频
            </h1>
            <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
              我们致力于打造中国领先的高清视频在线观看平台，为用户提供海量优质视频资源和极致的观影体验。
            </p>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-100">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-12">
            品牌故事
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground-900 mb-4">
                让每个人都能享受高品质视频内容
              </h3>
              <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                <p>
                  玖玖视频创立于2020年，创始团队由一群热爱视频内容的互联网人组成。我们深刻理解用户在视频观看中遇到的画质模糊、加载缓慢、广告繁多等痛点，决心打造一款真正以用户体验为核心的视频平台。
                </p>
                <p>
                  经过六年的持续打磨和迭代，玖玖视频已发展成为拥有超过10000部视频资源、覆盖10余个内容频道的综合性视频平台，月活跃用户突破500万。我们始终坚持「免费、高清、流畅」的产品理念，让每一位用户都能获得优质的视频观看体验。
                </p>
                <p>
                  未来，玖玖视频将继续加大在内容生态和技术研发上的投入，不断丰富视频资源库，提升播放技术和用户体验，努力成为国内最受用户喜爱的视频平台之一。
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {[
                { num: '10000+', label: '视频资源' },
                { num: '500万+', label: '月活用户' },
                { num: '10+', label: '内容频道' },
                { num: '99.9%', label: '可用率' },
              ].map((stat, i) => (
                <div key={i} className="p-6 rounded-lg bg-background-50 border border-background-200/70 text-center">
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">
                    {stat.num}
                  </div>
                  <div className="text-xs text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-12">
            发展历程
          </h2>

          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 md:-translate-x-px"></div>
            <div className="space-y-8">
              {milestones.map((item, index) => (
                <div key={index} className={`relative flex flex-col md:flex-row gap-4 md:gap-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'} pl-10 md:pl-0`}>
                    <div className="md:hidden absolute left-0 top-1.5 w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 z-10">
                      <i className="ri-check-line text-sm"></i>
                    </div>
                    <div className="hidden md:block">
                      <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-2">
                        {item.year}
                      </span>
                      <h4 className="text-base font-heading font-semibold text-foreground-900 mb-1">
                        {item.title}
                      </h4>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>

                  <div className="hidden md:flex w-8 flex-shrink-0 justify-center">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 z-10">
                      <i className="ri-check-line text-sm"></i>
                    </div>
                  </div>

                  <div className="flex-1" />

                  <div className="md:hidden pl-10">
                    <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-2">
                      {item.year}
                    </span>
                    <h4 className="text-base font-heading font-semibold text-foreground-900 mb-1">
                      {item.title}
                    </h4>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-100">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-4">
            用户口碑
          </h2>
          <p className="text-foreground-500 text-sm text-center mb-12">
            听听真实用户怎么说
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((item, index) => (
              <div key={index} className="p-6 rounded-lg bg-background-50 border border-background-200/70">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="w-4 h-4 flex items-center justify-center">
                      <i className={`text-sm ${i < item.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}></i>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  &ldquo;{item.content}&rdquo;
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 font-heading font-semibold text-sm">
                    {item.name[0]}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-900">{item.name}</div>
                    <div className="text-xs text-foreground-500">{item.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-4">
            联系我们
          </h2>
          <p className="text-foreground-500 text-sm text-center mb-10">
            如果您有任何问题或建议，欢迎随时与我们联系
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
            {[
              { icon: 'ri-mail-line', title: '电子邮箱', value: 'contact@jiujiu-video.com' },
              { icon: 'ri-phone-line', title: '客服热线', value: '400-888-9999' },
              { icon: 'ri-map-pin-line', title: '公司地址', value: '北京市海淀区中关村科技园' },
            ].map((item, i) => (
              <div key={i} className="p-5 rounded-lg bg-background-100 border border-background-200/70 text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className={`${item.icon} text-lg`}></i>
                </div>
                <h4 className="text-sm font-heading font-semibold text-foreground-900 mb-1">{item.title}</h4>
                <p className="text-xs text-foreground-500">{item.value}</p>
              </div>
            ))}
          </div>

          <div className="bg-background-100 rounded-lg p-6 md:p-8 border border-background-200/70">
            <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-6 text-center">
              在线留言
            </h3>

            {formSubmitted ? (
              <div className="text-center py-8">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-check-line text-2xl"></i>
                </div>
                <h4 className="text-lg font-heading font-semibold text-foreground-900 mb-2">感谢您的留言！</h4>
                <p className="text-sm text-foreground-500">我们会在24小时内回复您的反馈。</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form="true">
                {formStatus === 'error' && (
                  <div className="mb-4 p-3 rounded-md bg-primary-50 border border-primary-200 text-primary-700 text-sm">
                    {formError}
                  </div>
                )}

                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="anti-spam-field"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    id="contact-subject"
                    type="text"
                    name="subject"
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入留言主题"
                  />
                </div>

                <div className="mb-5">
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                    placeholder="请输入您的留言，不超过500个字符"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                </div>

                <button
                  type="submit"
                  className="w-full py-3 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap"
                >
                  提交留言
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
}